import { Alert, Box, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useContracts, useProject, useProjectSummary } from '@/api';
import { Panels } from '@/components/layouts';
import {
  BulkActionBar,
  ConflictDialog,
  PlanPanel,
  PlanProjectHeader,
  SaveStatusChip,
  SidePanel,
  PlanDrawerHeading,
} from '@/components/planning';
import { PlanEditorProvider } from '@/components/planning/editor';

/**
 * The Planning screen: project header, then a resizable split of the plan tree
 * against the contextual side panel.
 *
 * Two structural points, both fixes rather than preferences:
 *
 * - The header sits *outside* the scroll container. It used to be inside it, so
 *   the project name, section tabs and save-status chip scrolled out of sight as
 *   soon as you moved down a long plan.
 * - The split is a `Panels` group with an `autoSaveId`, so a width the user drags
 *   survives navigating to Financials and back.
 */
export const PlanningPage = () => {
  const { t } = useTranslation('pmPlanning');
  const { projectId } = useParams<{ projectId: string }>();
  const projectQuery = useProject(projectId);
  const contractsQuery = useContracts(projectId);
  const summaryQuery = useProjectSummary(projectId);
  if (projectQuery.isPending || contractsQuery.isPending) {
    return (
      <Box sx={{ p: 3 }} data-automation-id="planning-page">
        <Skeleton height={72} sx={{ mb: 2 }} />
        <Skeleton height={320} />
      </Box>
    );
  }

  const project = projectQuery.data;
  const contracts = contractsQuery.data?.contracts;

  if (projectQuery.error || contractsQuery.error || !project || !contracts) {
    return (
      <Box sx={{ p: 3 }} data-automation-id="planning-page">
        <Alert severity="error">
          {projectQuery.error?.message ??
            contractsQuery.error?.message ??
            t('page.ProjectNotFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      data-automation-id="planning-page"
    >
      <PlanEditorProvider project={project} contracts={contracts} summary={summaryQuery.data}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Panels autoSaveId="pm-planning-panels" collapseDrawerOnEscape>
            <Panels.Main
              /*
                The panel itself does not scroll: header and toolbar stay put
                and the plan card owns the scroll, so only the rows move. That
                is ADR 0013's decision 3, which was written and never built —
                the header has been inside the scroller since the commit that
                claimed otherwise.
              */
              panelContentProps={{ hideOverflow: true, hasPaddingBottom: false }}
              // Pinned bottom-right rather than beside the title: save state is
              // worth seeing wherever you are in a long plan, and the header
              // had four things competing for one line.
              footer={<SaveStatusChip />}
            >
              <PlanProjectHeader />
              <PlanPanel />
            </Panels.Main>
            <Panels.Drawer label={t('page.DrawerLabel')} heading={<PlanDrawerHeading />}>
              <SidePanel />
            </Panels.Drawer>
          </Panels>
        </Box>
        <BulkActionBar />
        <ConflictDialog />
      </PlanEditorProvider>
    </Box>
  );
};
